import React, { useState } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Avatar,
  // Badge,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  List,
} from '@mui/material';
import {
  Close as CloseIcon,
  Group as GroupIcon,
  InsertDriveFile as FileIcon,
  // Delete as DeleteIcon,
} from '@mui/icons-material';
import { Virtuoso } from 'react-virtuoso';
import { User, Conversation } from './chatTypes';
import { getInitials } from './chatUtils';
import chatProperties from './config/chatProperties';

interface SearchViewProps {
  onClose: () => void;
  users: User[];
  conversations: Conversation[];
  onSelectConversation: (conversation: Conversation) => void;
  isExpanded: boolean;
  searchLoading: boolean;
}

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    bgcolor: 'background.paper',
    animation: 'fadeIn 0.3s ease-out',
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
  },
  searchHeader: {
    p: 1.5,
    pb: 0.5,
    // borderBottom: 1,
    // borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  searchInputContainer: {
    position: 'relative',
    flex: 1,
    transition: 'all 0.2s ease',
  },
  searchIcon: {
    position: 'absolute',
    left: 1,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'action.active',
    transition: 'color 0.2s ease',
  },
  searchInput: {
    '& .MuiOutlinedInput-root': {
      fontSize: "14px",
      color: "#3D4C5E",
      borderRadius: '8px',
      minHeight: "30px",
      transition: 'all 0.2s ease, box-shadow 0.2s ease',
      '&.Mui-focused': {
        boxShadow: '0 0 0 2px rgba(61, 139, 248, 0.15)',
      },
      '&:hover': {
        borderColor: 'primary.main',
      },
    },
    minHeight: "30px"
  },
  filterContainer: {
    p: 1.5,
    // borderBottom: 1,
    // borderColor: 'divider',
    display: 'flex',
    gap: 1,
    overflowX: 'auto',
    transition: 'all 0.3s ease',
  },
  topUsers: {
    p: 1.5,
    borderBottom: 1,
    borderColor: 'divider',
  },
  topUsersGrid: {
    display: 'flex',
    gap: 2,
    overflowX: 'auto',
    pb: 1,
  },
  topUserItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 64,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
    '&:hover': {
      opacity: 0.8,
      transform: 'translateY(-2px)',
    },
  },
  searchResults: {
    flex: 1,
    overflowY: 'auto',
    // width: '100%',
    py: .5,
    m: 1, mt: 0,
    transition: 'opacity 0.3s ease',
  },
  resultItem: {
    // transition: 'all 0.1s ease',
    '&:hover': {
      bgcolor: 'rgba(61, 139, 248, 0.1)',
      // boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      transform: 'translateY(-4px)',
    },
    cursor: 'pointer',
    margin: "4px 0px",
    padding: "8px 8px",
    borderRadius: '8px',
    transition: 'all 0.15s ease',
    gap: 0
    // mx: 1,
    // my: 0.5,
  },
  historyItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '8px',
    px: 1,
    py: .5,
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: 'rgba(61, 139, 248, 0.1)',
      transform: 'translateX(4px)',
    },
    cursor: 'pointer',
  },
  closeButton: {
    transition: 'transform 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      transform: 'scale(1.1) rotate(90deg)',
      bgcolor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  listItemAvatar: {
    minWidth: '42px !important',
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: '14px',
    color: "#212121",
    fontWeight: "500",
    background: "#e5e7eb",
    minWidth: '32px',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'scale(1.1)',
    }
  },
  listItemContent: {
    // transition: 'transform 0.2s ease',
    // '&:hover': {
    //   transform: 'translateX(2px)',
    // },
  },
  skeleton: {
    height: 40,
    width: '100%',
  },
};

// Create a union type for the search results
type SearchResult = User | Conversation | { id: string; content: string; timestamp: Date };

const SearchView: React.FC<SearchViewProps> = ({
  onClose,
  users,
  conversations,
  onSelectConversation,
  searchLoading,
  // isExpanded
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  // const [activeFilter, setActiveFilter] = useState<SearchFilterType>('people');
  // const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([]);

  // const topUsers = [...users]
  //   .sort((a, b) => (b.interactionCount || 0) - (a.interactionCount || 0))
  //   .slice(0, 8);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // if (query && !searchHistory.some(h => h.query === query)) {
    //   const newHistory: SearchHistory = {
    //     id: Date.now().toString(),
    //     query,
    //     timestamp: new Date(),
    //   };
    //   setSearchHistory([newHistory, ...searchHistory].slice(0, 10));
    // }
  };

  // const removeFromHistory = (id: string) => {
  //   setSearchHistory(searchHistory.filter(h => h.id !== id));
  // };

  const handleUserClick = (user: User) => {
    const existingConversation = conversations.find(
      conv => !conv.isGroup && conv.participants[0].id === user.id
    );

    if (existingConversation) {
      onSelectConversation(existingConversation);
    } else {
      const newConversation: Conversation = {
        id: user.id,
        title: user.name,
        participants: [user],
        messages: [],
        unreadCount: 0,
        isGroup: false,
        createdAt: new Date(),
      };
      if (chatProperties.type === 'Agent') {
        newConversation.parentUserId = user.parentUserId;
      }
      onSelectConversation(newConversation);
    }
  };

  // const filterOptions = [
  //   { key: 'people', label: 'People' },
  //   // { key: 'agents', label: 'Agents' },
  //   // { key: 'groups', label: 'Groups' },
  //   { key: 'files', label: 'Files' },
  // ] as const;

  const getFilteredResults = () => {
    // if (!searchQuery) return [];

    // switch (activeFilter) {
    //   case 'people':
    //     return [...users.filter(user =>
    //       user.name.toLowerCase().includes(searchQuery.toLowerCase())
    //     ),
    //       // ...conversations.filter(conv =>
    //       //   conv.isGroup && conv.title.toLowerCase().includes(searchQuery.toLowerCase())
    //       // )
    //     ];
    //   case 'agents':
    //     return users.filter(user =>
    //       user.role === 'agent' &&
    //       user.name.toLowerCase().includes(searchQuery.toLowerCase())
    //     );
    //   case 'groups':
    //     return conversations.filter(conv =>
    //       conv.isGroup &&
    //       conv.title.toLowerCase().includes(searchQuery.toLowerCase())
    //     );
    //   case 'files':
    //     return conversations.flatMap(conv =>
    //       conv.messages.filter(msg =>
    //         msg.mediaUrl &&
    //         msg.content.toLowerCase().includes(searchQuery.toLowerCase())
    //       )
    //     );
    //   default:
    //     return [];
    // }
    return [...users.filter(user =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase())
    ),
      // ...conversations.filter(conv =>
      //   conv.isGroup && conv.title.toLowerCase().includes(searchQuery.toLowerCase())
      // )
    ];

  };

  const renderSearchResult = (index: number, result: SearchResult) => {
    if ('role' in result) {
      return (
        <ListItem
          key={result.id}
          sx={styles.resultItem}
          onClick={() => handleUserClick(result)}
        >
          <ListItemAvatar sx={styles.listItemAvatar}>
            {/* <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        color={
                          result.status === 'online'
                            ? 'success'
                            : result.status === 'busy'
                              ? 'error'
                              : result.status === 'away'
                                ? 'warning'
                                : 'default'
                        }
                      > */}
            <Avatar sx={styles.avatar}>{getInitials(result.name)}</Avatar>
            {/* </Badge> */}
          </ListItemAvatar>
          <ListItemText
            primary={result.name}
            sx={styles.listItemContent}
          // secondary={result.role}
          />
        </ListItem>
      );
    } else if ('isGroup' in result) {
      return (
        <ListItem
          key={result.id}
          sx={styles.resultItem}
          onClick={() => onSelectConversation(result)}
        >
          <ListItemAvatar>
            <Avatar sx={{
              bgcolor: 'primary.main',
              ...styles.avatar
            }}>
              <GroupIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={result.title}
            secondary={
              result.participants.slice(0, 2).map((p: User) => p.name).join(', ') +
              (result.participants.length > 2 ? ` +${result.participants.length - 2}` : '')
            }
            sx={styles.listItemContent}
          />
        </ListItem>
      );
    } else {
      return (
        <ListItem
          key={result.id}
          sx={styles.resultItem}
          onClick={() => {
            const conversation = conversations.find(conv =>
              conv.messages.some(msg => msg.id === result.id)
            );
            if (conversation) {
              onSelectConversation(conversation);
            }
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{
              bgcolor: 'grey.100',
              ...styles.avatar
            }}>
              <FileIcon color="action" />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={result.content}
            secondary={result.timestamp.toLocaleDateString()}
            sx={styles.listItemContent}
          />
        </ListItem>
      );
    }
  };

  const filteredResults = getFilteredResults();

  const SearchSkeleton = () => {
    return (
      <Box sx={styles.searchResults}>
        <List>
          {[...Array(4)].map((_, index) => (
            <ListItem
              key={index}
              sx={{ m: 0, p: 0, pb: 1 }}
            >
              <Skeleton sx={styles.skeleton} variant="rounded" />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.searchHeader}>
        <TextField
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search..."
          sx={styles.searchInput}
          autoFocus
          autoComplete="off"
        />
        <IconButton size="small" onClick={onClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </Box>
      {/* 
      <Box sx={styles.filterContainer}>
        {filterOptions.map((option) => (
          <Chip
            key={option.key}
            label={option.label}
            onClick={() => setActiveFilter(option.key)}
            color={activeFilter === option.key ? 'primary' : 'default'}
            size="small"
            sx={{
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              },
              '&.MuiChip-colorPrimary': {
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }
            }}
          />
        ))}
      </Box> */}

      <Box sx={styles.searchResults}>
        {searchLoading ? (
          <SearchSkeleton />
        ) : (
          <Virtuoso
            style={{ height: '100%' }}
            data={filteredResults}
            itemContent={renderSearchResult}
            totalCount={filteredResults.length}
            overscan={20}
          />
        )}
      </Box>
    </Box>
  );
};

{/* <Box sx={styles.topUsers}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Frequent Contacts
            </Typography>
            <Box sx={styles.topUsersGrid}>
              {topUsers.map((user) => (
                <Box
                  key={user.id}
                  sx={styles.topUserItem}
                  onClick={() => handleUserClick(user)}
                >
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    color={
                      user.status === 'online'
                        ? 'success'
                        : user.status === 'busy'
                          ? 'error'
                          : user.status === 'away'
                            ? 'warning'
                            : 'default'
                    }
                  >
                    <Avatar sx={{ width: 48, height: 48, mb: 1 }}>
                      {getInitials(user.name)}
                    </Avatar>
                  </Badge>
                  <Typography variant="caption" noWrap>
                    {user.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Recent Searches
            </Typography>
            <List sx={{ p: 0, overflowY: 'auto', maxHeight: isExpanded ? 'calc(100vh - 470px)' : '200px' }}>
              {searchHistory.map((history) => (
                <ListItem
                  key={history.id}
                  sx={styles.historyItem}
                  onClick={() => handleSearch(history.query)}
                >
                  <Typography variant="body2">{history.query}</Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromHistory(history.id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box> */}

export default SearchView;